import type { PlayerStats, PlayerStatsSnapshot } from '../gameplay/player';
import type { AudioManager } from '../audio';

/** Red vignette pulse + audio cue every time the player takes damage —
 *  hull (in-ship) or health (on-foot). Subscribes directly to PlayerStats
 *  so any source — projectiles, asteroid rams, surface collisions, falls —
 *  feeds the same feedback channel without each damage site having to
 *  remember to play the SFX. */
export class DamageFlashLayer {
  readonly element: HTMLElement;
  private readonly unsubscribe: () => void;
  private lastHull: number;
  private lastHealth: number;
  private fadeTimer: number | null = null;

  constructor(stats: PlayerStats, private readonly audio: AudioManager) {
    this.element = document.createElement('div');
    this.element.className = 'damage-flash';
    this.lastHull = stats.hull;
    this.lastHealth = stats.health;
    this.unsubscribe = stats.subscribe((snap) => this.handle(snap));
  }

  dispose(): void {
    this.unsubscribe();
    if (this.fadeTimer !== null) {
      window.clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
    this.element.remove();
  }

  private handle(snap: PlayerStatsSnapshot): void {
    const hullLost = this.lastHull - snap.hull;
    const healthLost = this.lastHealth - snap.health;
    this.lastHull = snap.hull;
    this.lastHealth = snap.health;

    // Take whichever pool just lost more (in proportion to its max) so a
    // small health hit on the astronaut isn't drowned out by an idle hull
    // change of equal absolute value, and vice-versa.
    const hullRatio = hullLost > 0 ? hullLost / Math.max(snap.hullMax, 1) : 0;
    const healthRatio =
      healthLost > 0 ? healthLost / Math.max(snap.healthMax, 1) : 0;
    const ratio = Math.max(hullRatio, healthRatio);
    if (ratio <= 0) return;

    // Intensity scales with the chunk of max-pool lost — single shots
    // barely tint the screen, big collisions paint it red. Capped so even
    // a near-death blow doesn't completely block the view.
    const normalised = Math.min(1, ratio / 0.18);
    const opacity = 0.35 + normalised * 0.55;

    this.trigger(opacity);
    this.audio.playHullDamage(0.4 + normalised * 0.6);
  }

  private trigger(opacity: number): void {
    const el = this.element;
    el.style.setProperty('--flash-opacity', opacity.toFixed(3));
    // Restart the CSS transition by clearing the active class for a tick.
    el.classList.remove('is-active');
    // Force reflow so the next add re-runs the snap-on transition.
    void el.offsetWidth;
    el.classList.add('is-active');

    if (this.fadeTimer !== null) window.clearTimeout(this.fadeTimer);
    // Hold the flash at peak briefly, then let the slow fade-out transition
    // take it back to 0 — feels punchy without becoming a strobe.
    this.fadeTimer = window.setTimeout(() => {
      el.classList.remove('is-active');
      this.fadeTimer = null;
    }, 80);
  }
}
